import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Card } from "react-bootstrap";
import { BsArrowLeft } from "react-icons/bs";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import cpfMask from "../../../components/Formatters/cpfMask";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";

const statusProcessoMap = {
  P: "Aberto",
  C: "Cancelado",
  B: "Baixado",
  A: "Aguardando Checklist",
  E: "Em ação fiscal",
  D: "PDA",
};

const modoCienciaOptions = [
  { key: "0", value: "", text: "Selecione" },
  { key: "1", value: "A", text: "AR" },
  { key: "2", value: "P", text: "Pessoal" },
  { key: "3", value: "E", text: "Edital" },
];

const validationSchema = Yup.object().shape({
  sqMomento: Yup.string().required("Momento é obrigatório"),
  tpModoCiencia: Yup.string().required("Modo de ciência é obrigatório"),
  dtCiencia: Yup.string().when("tpModoCiencia", {
    is: (val) => val && val !== "E",
    then: Yup.string().required("Data de ciência é obrigatória"),
    otherwise: Yup.string().default(""),
  }),
  dtPublicacaoEdital: Yup.string().when("tpModoCiencia", {
    is: "E",
    then: Yup.string().required("Data de publicação do edital é obrigatória"),
    otherwise: Yup.string().default(""),
  }),
  txJustificativa: Yup.string().required("A justificativa é obrigatória").max(2000, "Máximo de 2000 caracteres"),
  nuCpf: Yup.string().default(""),
  nmContribuinte: Yup.string().default(""),
  txCargo: Yup.string().default(""),
});

const brToIso = (br) => (br && br.includes("/") ? br.split("/").reverse().join("-") : "");
const isoToBr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

const EditarCiencia = ({ location, history }) => {
  const sqProcesso = location?.state?.sqProcesso;
  const cdProcesso = location?.state?.cdProcesso;
  const sqAcaoFiscal = location?.state?.sqAcaoFiscal;
  const ciencia = location?.state?.ciencia;

  const [momentoOptions, setMomentoOptions] = useState([{ key: "0", value: "", text: "Selecione" }]);
  const [dadosProcesso, setDadosProcesso] = useState(null);
  const [cpfEncontrado, setCpfEncontrado] = useState(false);
  const [cargoEncontrado, setCargoEncontrado] = useState(false);
  const [erroAlteracao, setErroAlteracao] = useState(null);

  const { request: requestMomentos, data: dataMomentos } = useApiClient();
  const { request: requestDadosProcesso, data: dataDadosProcesso } = useApiClient();
  const {
    request: requestAlterar,
    data: dataAlterar,
    errors: errorsAlterar,
    isLoading: loadingAlterar,
  } = useApiClient();
  const { request: requestCpf, data: dataCpf } = useApiClient();

  // Carregar dados auxiliares
  useEffect(() => {
    if (sqProcesso) {
      requestDadosProcesso(apiRoutes.fiscalizacao.pat.consultarDadosProcesso, { SqProcesso: sqProcesso });
      requestMomentos(apiRoutes.fiscalizacao.pat.listaMomentoDisponivel, { SqProcesso: sqProcesso });
    }
  }, [sqProcesso, requestDadosProcesso, requestMomentos]);

  useEffect(() => {
    if (dataDadosProcesso?.result) {
      const dados = Array.isArray(dataDadosProcesso.result) ? dataDadosProcesso.result[0] : dataDadosProcesso.result;
      setDadosProcesso(dados);
    }
  }, [dataDadosProcesso]);

  useEffect(() => {
    if (dataMomentos?.result) {
      const baseOptions = dataMomentos.result.map((item, index) => ({
        key: String(index + 1),
        value: String(item.sqMomento),
        text: item.dsMomentoAnalisado,
      }));
      const sqOriginal = ciencia?.sqMomento ? String(ciencia.sqMomento) : "";
      const jaTem = baseOptions.some((o) => o.value === sqOriginal);
      const options = [{ key: "0", value: "", text: "Selecione" }, ...baseOptions];
      if (sqOriginal && !jaTem) {
        options.push({
          key: "orig",
          value: sqOriginal,
          text: ciencia?.dsMomentoAnalisado || "(momento original)",
        });
      }
      setMomentoOptions(options);
    }
  }, [dataMomentos, ciencia]);

  const extrairMensagemErro = (msg) => {
    if (!msg || typeof msg !== "string") return "Erro ao alterar ciência.";
    const matchErro = msg.match(/Erro:\s*(.+?)(?:\s+-\s+(?:em|at)\s+|$)/s);
    if (matchErro) return matchErro[1].trim();
    const semStack = msg.split(/\s+-\s+(?:em|at)\s+/)[0];
    return semStack.trim() || "Erro ao alterar ciência.";
  };

  useEffect(() => {
    if (dataAlterar?.result && !loadingAlterar) {
      if (typeof dataAlterar.result === "string" && dataAlterar.result.includes("Lamentamos")) {
        setErroAlteracao({ message: extrairMensagemErro(dataAlterar.result) });
      } else {
        setErroAlteracao(null);
        toast.success("Ciência alterada com sucesso!");
        history.goBack();
      }
    }
    if (errorsAlterar && !loadingAlterar) {
      const msg = errorsAlterar?.message || errorsAlterar;
      setErroAlteracao({ message: extrairMensagemErro(typeof msg === "string" ? msg : "") });
    }
  }, [dataAlterar, errorsAlterar, loadingAlterar]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sqMomento: ciencia?.sqMomento ? String(ciencia.sqMomento) : "",
      tpModoCiencia: ciencia?.tpModoCiencia || "",
      dtCiencia: brToIso(ciencia?.dtCiencia),
      dtPublicacaoEdital: brToIso(ciencia?.dtPublicacaoEdital),
      txJustificativa: "",
      nuCpf: ciencia?.nuCpf ? cpfMask(ciencia.nuCpf) : "",
      nmContribuinte: ciencia?.nmContribuinte || "",
      txCargo: ciencia?.txCargo || "",
    },
    validationSchema,
    onSubmit: (values) => {
      const params = {
        SqProcesso: sqProcesso,
        SqMomentoOriginal: ciencia?.sqMomento ? String(ciencia.sqMomento) : "",
        SqMomento: values.sqMomento,
        SqMomentoCiencia: ciencia?.sqMomentoCiencia,
        TxJustificativa: values.txJustificativa,
        TpModoCiencia: values.tpModoCiencia,
        DtCiencia: isoToBr(values.dtCiencia),
        DtPublicacaoEdital: isoToBr(values.dtPublicacaoEdital),
        FgDecisaoDefinitiva: ciencia?.fgDecisaoDefinitiva || "S",
        NuCpf: values.nuCpf.replace(/\D/g, ""),
        NmContribuinte: values.nmContribuinte,
        TxCargo: values.txCargo,
      };
      requestAlterar(apiRoutes.fiscalizacao.pat.alterarMomentoCiencia, params);
    },
  });

  const isEdital = formik.values.tpModoCiencia === "E";

  const buscaCpf = () => {
    const cpf = formik.values.nuCpf.replace(/\D/g, "");
    if (cpf.length === 11) {
      requestCpf(apiRoutes.fiscalizacao.ordemservico.consultaRepresentante, {
        cpfRepresentante: formik.values.nuCpf,
        sqAcaoFiscal: sqAcaoFiscal,
      });
    } else {
      setCpfEncontrado(false);
      setCargoEncontrado(false);
    }
  };

  useEffect(() => {
    if (dataCpf) {
      const resultado = dataCpf.result?.[0];
      const nome = resultado?.nomeRepresentante || "";
      const cargo = resultado?.cargoRepresentante || "";
      formik.setValues((curr) => ({
        ...curr,
        nuCpf: cpfMask(curr.nuCpf),
        nmContribuinte: nome || curr.nmContribuinte,
        txCargo: cargo || curr.txCargo,
      }));
      setCpfEncontrado(nome.length > 0);
      setCargoEncontrado(cargo.length > 0);
    }
  }, [dataCpf]);

  useEffect(() => {
    if (isEdital) {
      formik.setFieldValue("dtCiencia", "");
    } else {
      formik.setFieldValue("dtPublicacaoEdital", "");
    }
  }, [formik.values.tpModoCiencia]);

  if (!sqProcesso || !ciencia) {
    return (
      <div className="container-fluid">
        <h1 className="h3 mb-4 text-gray-800">Alterar Ciência</h1>
        <div className="alert alert-warning">Ciência não informada. Volte à tela anterior.</div>
      </div>
    );
  }

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">
        <Button variant="link" className="p-0 mr-2" onClick={() => history.goBack()}>
          <BsArrowLeft size={22} />
        </Button>
        Alterar Ciência - PAT {cdProcesso}
      </h1>

      <div className="row">
        <div className="col-lg-12">
          {dadosProcesso && (
            <div className="p-4">
              <Row>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">Inscrição Estadual</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nuInscEstadual || "-"}</Card.Text>
                </Col>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">{dadosProcesso.tpPessoa}</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nuDocumento || "-"}</Card.Text>
                </Col>
                <Col xs="12" lg="3" className="mb-2">
                  <Card.Title as="strong">Interessado</Card.Title>
                  <Card.Text as="p">{dadosProcesso.nmFormal}</Card.Text>
                </Col>
                <Col xs="6" lg="3" className="mb-2">
                  <Card.Title as="strong">Protocolo</Card.Title>
                  <Card.Text as="p">{dadosProcesso.cdPgeProcesso || "-"}</Card.Text>
                </Col>
                <Col xs="6" lg="2" className="mb-2">
                  <Card.Title as="strong">Status</Card.Title>
                  <Card.Text as="p">
                    {statusProcessoMap[dadosProcesso.stProcesso] || dadosProcesso.stProcesso}
                  </Card.Text>
                </Col>
              </Row>
            </div>
          )}

          <Card className="shadow mb-4">
            <Card.Header className="py-3">
              <h6 className="font-weight-bold text-primary m-0">Alterar Ciência</h6>
            </Card.Header>
            <Card.Body>
              <FormAlert formik={formik} errors={erroAlteracao} isLoading={loadingAlterar} dismissible />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Momento</Form.Label>
                    <SelectInput formik={formik} field="sqMomento" options={momentoOptions} />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Modo de Ciência</Form.Label>
                    <SelectInput formik={formik} field="tpModoCiencia" options={modoCienciaOptions} />
                  </Form.Group>
                  {isEdital ? (
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Data Publicação Edital</Form.Label>
                      <DateTimeInputFormik formik={formik} field="dtPublicacaoEdital" />
                    </Form.Group>
                  ) : (
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Data de Ciência</Form.Label>
                      <DateTimeInputFormik formik={formik} field="dtCiencia" />
                    </Form.Group>
                  )}
                </Row>
                <Row className="mt-2">
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>CPF</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o CPF"
                      formik={formik}
                      field="nuCpf"
                      maxLength="14"
                      evento={buscaCpf}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Nome do Contribuinte</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o nome"
                      formik={formik}
                      field="nmContribuinte"
                      disabled={cpfEncontrado}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={12} md={4}>
                    <Form.Label>Cargo</Form.Label>
                    <TextInputFormik
                      placeholder="Informe o cargo"
                      formik={formik}
                      field="txCargo"
                      disabled={cargoEncontrado}
                    />
                  </Form.Group>
                </Row>
                <Row className="mt-2">
                  <Form.Group as={Col} sm={12}>
                    <Form.Label>Justificativa</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      maxLength={2000}
                      placeholder="Informe a justificativa para a alteração"
                      name="txJustificativa"
                      value={formik.values.txJustificativa}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={formik.touched.txJustificativa && !!formik.errors.txJustificativa}
                    />
                    <Form.Control.Feedback type="invalid">{formik.errors.txJustificativa}</Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row className="mt-3">
                  <Form.Group as={Row} style={{ marginLeft: 0 }} className="d-flex flex-row">
                    <Col sm="12">
                      <Button className="btn btn-primary btn-md mr-2" type="submit" disabled={loadingAlterar}>
                        Salvar
                      </Button>
                      <Button className="btn btn-primary btn-md mr-2" variant="light" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    </Col>
                  </Form.Group>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EditarCiencia;
